import { MessageCircle, Mail, Music2, Instagram, Clock } from "lucide-react";
import Reveal from "./Reveal";
import { SiteLogo } from "../SiteLogo";

export function WhatsAppFloat({ settings }) {
  const number = (settings?.whatsapp_number || "").replace(/[^0-9]/g, "");
  if (!number) return null;
  return (
    <a
      href={`https://wa.me/${number}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-button"
      aria-label="WhatsApp ile iletişime geç"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#0047FF] to-[#00F0FF] flex items-center justify-center shadow-[0_0_24px_rgba(0,240,255,0.4)] hover:scale-110 transition-transform"
    >
      <MessageCircle size={24} strokeWidth={1.5} className="text-white" />
    </a>
  );
}

export default function Contact({ settings }) {
  const number = settings?.whatsapp_number || "";
  const waDigits = number.replace(/[^0-9]/g, "");

  const items = [
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: number || "Yakında eklenecek",
      href: waDigits ? `https://wa.me/${waDigits}` : null,
      testid: "contact-whatsapp",
    },
    {
      icon: Mail,
      label: "E-posta",
      value: settings?.email || "Yakında eklenecek",
      href: settings?.email ? `mailto:${settings.email}` : null,
      testid: "contact-email",
    },
    {
      icon: Music2,
      label: "TikTok",
      value: settings?.tiktok_url ? "TikTok'ta takip et" : "Yakında eklenecek",
      href: settings?.tiktok_url || null,
      testid: "contact-tiktok",
    },
    {
      icon: Instagram,
      label: "Instagram",
      value: settings?.instagram_url ? "Instagram'da takip et" : "Yakında eklenecek",
      href: settings?.instagram_url || null,
      testid: "contact-instagram",
    },
    {
      icon: Clock,
      label: "Çalışma Saatleri",
      value: settings?.working_hours || "Hafta içi 09:00 - 22:00",
      href: null,
      testid: "contact-hours",
    },
  ];

  return (
    <section id="iletisim" data-testid="contact-section" className="relative py-28 bg-[#050505]">
      <div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-[#0047FF]/8 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">İletişim</span>
            <h2 className="font-display font-700 text-3xl md:text-4xl mt-4 text-white tracking-tight">
              Bize <span className="text-gradient">Ulaşın</span>
            </h2>
            <p className="mt-5 text-[#A0ABC0]">Sorularınız için bize dilediğiniz kanaldan ulaşabilirsiniz.</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => {
            const Wrapper = item.href ? "a" : "div";
            return (
              <Reveal key={item.label} delay={(i % 3) * 80}>
                <Wrapper
                  {...(item.href ? { href: item.href, target: "_blank", rel: "noopener noreferrer" } : {})}
                  data-testid={item.testid}
                  className="card-lift flex items-center gap-4 rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-6"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#0047FF]/15 border border-cyan-400/20 flex items-center justify-center shrink-0">
                    <item.icon size={19} strokeWidth={1.5} className="text-cyan-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[#64748B] font-medium">{item.label}</p>
                    <p className="text-sm font-semibold text-white truncate mt-0.5">{item.value}</p>
                  </div>
                </Wrapper>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Footer({ settings }) {
  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const waDigits = (settings?.whatsapp_number || "").replace(/[^0-9]/g, "");

  const navLinks = [
    { id: "anasayfa", label: "Ana Sayfa" },
    { id: "hizmetler", label: "Hizmetlerimiz" },
    { id: "yayincilar", label: "Yayıncılarımız" },
    { id: "basvuru", label: "Başvuru" },
    { id: "sss", label: "Sıkça Sorulan Sorular" },
    { id: "iletisim", label: "İletişim" },
  ];
  const legalLinks = ["Gizlilik Politikası", "KVKK Aydınlatma Metni", "Çerez Politikası", "Kullanım Koşulları"];

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/[0.06] bg-[#04060B] pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="mb-5">
              <SiteLogo />
            </div>
            <p className="text-sm text-[#A0ABC0] leading-relaxed max-w-sm">
              TikTok canlı yayın ajansı olarak yayıncılarımıza profesyonel yönetim, stratejik büyüme ve kesintisiz destek sunuyoruz.
            </p>
            <div className="flex gap-3 mt-6">
              {settings?.tiktok_url && (
                <a href={settings.tiktok_url} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" aria-label="TikTok"
                  className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                  <Music2 size={16} strokeWidth={1.5} />
                </a>
              )}
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram"
                  className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                  <Instagram size={16} strokeWidth={1.5} />
                </a>
              )}
              {waDigits && (
                <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" aria-label="WhatsApp"
                  className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                  <MessageCircle size={16} strokeWidth={1.5} />
                </a>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-[#64748B] mb-5">Sayfalar</h4>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.id}>
                  <button onClick={() => goTo(l.id)} data-testid={`footer-link-${l.id}`} className="text-sm text-[#A0ABC0] hover:text-cyan-300 transition-colors">
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold tracking-[0.15em] uppercase text-[#64748B] mb-5">Yasal</h4>
            <ul className="space-y-3">
              {legalLinks.map((l) => (
                <li key={l}>
                  <span data-testid={`footer-legal-${l}`} className="text-sm text-[#A0ABC0] hover:text-cyan-300 transition-colors cursor-pointer">
                    {l}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-white/[0.06] text-center">
          <p data-testid="footer-copyright" className="text-xs text-[#64748B]">© 2026 Hoom Agency. Tüm hakları saklıdır.</p>
        </div>
      </div>
    </footer>
  );
}
